/**
 * Projects an AHP `ChatState` transcript into {@link Artifact}s.
 *
 * Used by quick chat, embedded chat, and AHP-environment agent sessions. See
 * `artifact-core` for the shared derivation and its coverage gaps.
 *
 * The fields read here are all reconstructed from persisted actions rather than
 * populated only by live streaming, which is what makes the list identical
 * after a reload:
 *   - `toolInput` is set by `chat/toolCallReady` and carried forward verbatim
 *     by `chat/toolCallComplete` (`toolInput: tc.toolInput`). While a call is
 *     still `streaming` it is `undefined` and only `partialInput` accumulates,
 *     but such a call is not `completed` and so is excluded anyway.
 *   - `status`/`success` come from `chat/toolCallComplete`'s `result`.
 * `replayTaskAhpEvents` folds those same actions through the same
 * `AhpChatStore`/`chatReducer` the live relay uses, differing only at the
 * transport boundary (chunk reassembly, sequence checks).
 */

import {getTurns} from '@github-ui/ahp-relay/ahp-session-selectors'
import {inlineToolInput} from '@github-ui/ahp-relay/tool-input'
import type {ChatState, ToolCallState} from '@microsoft/agent-host-protocol'

import {collectArtifacts, isRecord, type Artifact, type ToolWriteRecord} from './artifact-core'

const PART_TOOL_CALL = 'toolCall'
const STATUS_COMPLETED = 'completed'

/**
 * Parse an AHP `toolInput` into an arguments object.
 *
 * AHP sends the input as a JSON *string*, holding either a JSON object for
 * multi-argument tools or the bare primary scalar for single-argument ones
 * (e.g. just the path for `create`). This mirrors the coercion in
 * `ahp-tool-helpers`, narrowed to the keys this module needs.
 */
function parseToolArgs(toolCall: ToolCallState, primaryKey: string): Record<string, unknown> {
  const raw = inlineToolInput('toolInput' in toolCall ? toolCall.toolInput : undefined)
  if (!raw) return {}

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    parsed = undefined
  }

  if (isRecord(parsed)) return parsed
  return {[primaryKey]: typeof parsed === 'string' ? parsed : raw}
}

/** A settled, successful tool call. Failed and in-flight writes are ignored. */
function succeeded(toolCall: ToolCallState): boolean {
  return toolCall.status === STATUS_COMPLETED && 'success' in toolCall && toolCall.success === true
}

function resultText(toolCall: ToolCallState): string | undefined {
  if (!('content' in toolCall) || !Array.isArray(toolCall.content)) return undefined
  const text = toolCall.content
    .map(block => (block.type === 'text' ? block.text : ''))
    .join('')
    .trim()
  return text || undefined
}

function* toolWriteRecords(state: ChatState): Generator<ToolWriteRecord> {
  for (const turn of getTurns(state)) {
    for (const part of turn.responseParts ?? []) {
      if (part.kind !== PART_TOOL_CALL) continue

      const {toolCall} = part
      const preview = resultText(toolCall)
      yield {
        toolCallId: toolCall.toolCallId,
        toolName: toolCall.toolName,
        resolveArgs: primaryKey => parseToolArgs(toolCall, primaryKey),
        succeeded: succeeded(toolCall),
        ...(preview === undefined ? {} : {preview}),
      }
    }
  }
}

/** Reduce an AHP chat transcript to the files the agent wrote. */
export function deriveArtifactsFromChatState(state: ChatState | undefined): Artifact[] {
  if (!state) return []
  return collectArtifacts(toolWriteRecords(state))
}
